import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, ElementRef, Input, Pipe, PipeTransform, Output, EventEmitter, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { FormBuilder, Validators, ControlContainer, FormGroupDirective, UntypedFormControl, FormGroup } from '@angular/forms';
import { interval, Subscription, Subject } from 'rxjs';
import { debounceTime, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { ActivatedRoute, Router } from '@angular/router';

import { InventoryService } from '../../services/inventory.service'
import { GlobalsService } from '../../services/globals.service';
import { trigger, state, style, transition, animate, query, group, stagger } from '@angular/animations';

import { Location } from '@angular/common'

@Component({
	selector: 'app-item-barcode-add',
	templateUrl: './item-barcode-add.component.html',
	styleUrls: ['./item-barcode-add.component.scss']
})
export class ItemBarcodeAddComponent implements OnInit, OnDestroy {
	@Input() searchedfor: string = '';
	@Output() barcode_added = new EventEmitter<any>();
	
	// Form controls
	itemSearchForm = new UntypedFormControl('');
	barcodeForm = new UntypedFormControl('');
	
	// State management
	inventorysearching: any = false;
	sending = false;
	searching = false;
	errorMessage: string = '';
	
	// Data
	allitems: any = [];
	filteredItems: any = [];
	selecteditem: any = false;
	
	// Mobile optimization
	isMobile: boolean = false;
	
	// Cleanup
	private destroy$ = new Subject<void>();

	constructor(private inventoryService: InventoryService) { }

	ngOnInit(): void {
		// Detect mobile device
		this.detectMobile();
		
		// Set initial barcode value
		this.barcodeForm.setValue(this.searchedfor);
		
		// Setup search with debouncing for better mobile performance
		this.itemSearchForm.valueChanges
			.pipe(
				debounceTime(300), // Wait 300ms after user stops typing
				distinctUntilChanged(), // Only emit when value actually changes
				takeUntil(this.destroy$)
			)
			.subscribe(newValue => {
				this.itemSearch(newValue);
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		
		// Cleanup any ongoing searches
		if (this.inventorysearching) {
			this.inventorysearching.unsubscribe();
		}
	}

  selectItem(item: any): void {
    this.selecteditem = item;
    this.itemSearchForm.setValue('');
    this.allitems = [];
    this.filteredItems = [];
    this.errorMessage = ''; // Clear any previous errors
    
    // Auto-focus barcode input on both mobile and desktop
    setTimeout(() => {
      const barcodeInput = document.querySelector('.mobile-barcode-input') as HTMLInputElement;
      if (barcodeInput) {
        barcodeInput.focus();
      }
    }, 100);
  }

  goBackToSearch(): void {
    this.selecteditem = false;
    this.barcodeForm.setValue(this.searchedfor);
    this.errorMessage = '';
    
    // Focus search input on both mobile and desktop
    setTimeout(() => {
      const searchInput = document.querySelector('.mobile-search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  }

	itemSearch(searchTerm: string): void {
		if (!searchTerm || searchTerm.trim() === '') {
			this.filteredItems = [];
			this.searching = false;
			return;
		}

		this.searching = true;
		this.errorMessage = '';
		
		// Cancel previous search
		if (this.inventorysearching) {
			this.inventorysearching.unsubscribe();
		}

		this.inventorysearching = this.inventoryService.itemCleanSearch(searchTerm)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (items: any) => {
					this.allitems = items || [];
					this.filteredItems = items || [];
					this.searching = false;
				},
				error: (error) => {
					console.error('Search error:', error);
					this.errorMessage = 'Search failed. Please try again.';
					this.searching = false;
					this.filteredItems = [];
				}
			});
	}

	saveBarcode(): void {
		if (!this.selecteditem || !this.barcodeForm.value) {
			this.errorMessage = 'Please select an item and enter a barcode.';
			return;
		}

		// Mobile-friendly confirmation
		const confirmMessage = `Add barcode "${this.barcodeForm.value}" to ${this.selecteditem.stockid}?`;
		
		if (confirm(confirmMessage)) {
			this.sending = true;
			this.errorMessage = '';
			
			const data = { 
				stockid: this.selecteditem.stockid, 
				barcode: this.barcodeForm.value 
			};
			
			this.inventoryService.addBarcodeClean(data)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					next: (result: any) => {
						this.sending = false;
						if (result.success) {
							this.barcode_added.emit(this.selecteditem);
						} else {
							this.errorMessage = result.message || 'Failed to add barcode. Please try again.';
						}
					},
					error: (error) => {
						console.error('Save barcode error:', error);
						this.sending = false;
						this.errorMessage = 'Failed to add barcode. Please check your connection and try again.';
					}
				});
		}
	}

	// Track by function for better performance with large lists
	trackByItem(index: number, item: any): string {
		return item.stockid || index;
	}

	// Mobile detection
	private detectMobile(): void {
		this.isMobile = window.innerWidth <= 768 || 
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
	}

	// Clear error message
	clearError(): void {
		this.errorMessage = '';
	}
}
